import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { aiStageLabel } from '../shared/brand/aiProgress/aiRun';
import BrandLoader from '../shared/brand/BrandLoader';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import Header from '../shared/TailwindComponents/Header';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import Table from '../shared/TailwindComponents/Table';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import {
  PiFileMagnifyingGlassLight,
  PiPlusLight,
  PiSpinnerGapLight,
  PiTrashLight,
  PiPencilSimpleLight,
  PiShieldCheckLight,
  PiWarningCircleLight,
  PiClockLight,
  PiCheckCircleLight,
  PiFileTextLight,
  PiNotepadLight,
  PiArrowCounterClockwiseLight,
  PiTrashSimpleLight,
  PiEyeLight,
  PiClockCounterClockwiseLight,
} from 'react-icons/pi';

import {
  fetchReviewSessions,
  deleteReviewSession,
  bulkDeleteReviewSessions,
  renameReviewSession,
  restoreReviewSession,
} from '../../store/actions/Review/ReviewActions';
import { hideModal, showModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, reviewListTourSteps } from '../shared/FeatureTour';
import { ReviewSessionListItem, ReviewSessionStatus } from '../../types/review';
import { useProcessingPubSub } from '../../hooks/useProcessingPubSub';
import StartReviewForm from './StartReviewForm';
import DeleteReviewConfirmation from './DeleteReviewConfirmation';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import debounce from 'lodash.debounce';
import { formatDateTime, getIntlLocale } from '../../utils/functions';
import Button from '../shared/TailwindComponents/Button';
import ResourceTrash from '../RecycleBin/ResourceTrash';
import AuditLogPanel from '../AuditLog/AuditLogPanel';

import ActivityDrawer from '../shared/activity/ActivityDrawer';

// ─── Status helpers ─────────────────────────────────────────────────────────

const statusColorMap: Record<ReviewSessionStatus, BadgeColor> = {
  pending: 'gray',
  processing: 'blue',
  completed: 'indigo',
  completed_with_errors: 'orange',
  failed: 'red',
  pending_sign_off: 'yellow',
  finalized: 'green',
};

const statusLabelMap: Record<ReviewSessionStatus, string> = {
  pending: 'Queued',
  processing: 'Processing',
  completed: 'In Review',
  completed_with_errors: 'In Review',
  failed: 'Failed',
  pending_sign_off: 'Awaiting Sign-Off',
  finalized: 'Finalized',
};

const statusIconMap: Record<ReviewSessionStatus, React.ReactNode> = {
  pending: <PiClockLight className="w-3.5 h-3.5" />,
  processing: <BrandLoader size="xs" className="!h-3.5 !w-3.5" />,
  completed: <PiNotepadLight className="w-3.5 h-3.5" />,
  completed_with_errors: <PiWarningCircleLight className="w-3.5 h-3.5" />,
  failed: <PiWarningCircleLight className="w-3.5 h-3.5" />,
  pending_sign_off: <PiShieldCheckLight className="w-3.5 h-3.5" />,
  finalized: <PiCheckCircleLight className="w-3.5 h-3.5" />,
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatDate = (iso: string | null) => {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  return d.toLocaleDateString(getIntlLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatRelativeTime = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(iso);
};

const getRiskLevel = (score: number | null): { label: string; color: BadgeColor; textColor: string } => {
  if (score === null || score === undefined) return { label: '\u2014', color: 'gray', textColor: 'text-gray-400' };
  if (score >= 8) return { label: 'Critical', color: 'red', textColor: 'text-danger-600 dark:text-danger-400' };
  if (score >= 6) return { label: 'High', color: 'orange', textColor: 'text-warning-600 dark:text-warning-400' };
  if (score >= 4) return { label: 'Medium', color: 'yellow', textColor: 'text-warning-600 dark:text-warning-400' };
  return { label: 'Low', color: 'green', textColor: 'text-success-600 dark:text-success-400' };
};

// ─── Name Cell (rich, like Matters) ─────────────────────────────────────────

const ReviewNameCell: React.FC<{
  name: string | null;
  isProcessing: boolean;
  isFailed: boolean;
  isCompleted: boolean;
  isNew: boolean;
  liveStep?: string;
  liveMessage?: string;
  livePercent?: number;
  t: any;
}> = ({
  name,
  isProcessing,
  isFailed,
  isCompleted,
  isNew,
  liveStep,
  liveMessage,
  livePercent,
  t,
}) => {
  const displayName = name || t('Untitled review');

  if (isProcessing) {
    return (
      <div className="flex items-center gap-2.5 min-w-0 pe-2">
        <BrandLoader size="xs" className="!h-4 !w-4 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          {liveStep ? (
            <>
              <div className="text-sm text-info-600 dark:text-info-400 font-medium truncate">
                {t(aiStageLabel(liveStep))}
              </div>
              {liveMessage && (
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                  {liveMessage}
                </div>
              )}
              {livePercent != null && livePercent > 0 && (
                <div className="mt-1 h-1 w-full max-w-[180px] bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-info-500 to-info-700 transition-all duration-700 ease-in-out rounded-full"
                    style={{ width: `${livePercent}%` }}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                {t('Analyzing documents...')}
              </div>
              <div className="h-2.5 w-2/3 bg-gray-200 dark:bg-gray-600 rounded mt-1 animate-fadePulse" />
            </>
          )}
        </div>
      </div>
    );
  }

  if (isFailed) {
    return (
      <div className="flex items-center gap-2.5 min-w-0 pe-2 opacity-60">
        <div className="flex-1 min-w-0">
          <div className="text-sm text-danger-500 dark:text-danger-400 italic truncate">
            {t('Processing failed')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0 pe-2">
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
          {displayName}
        </span>
        {isNew && <Badge color="green">{t('New')}</Badge>}
      </div>
    </div>
  );
};

// ─── Risk Score Cell ────────────────────────────────────────────────────────

const RiskScoreCell: React.FC<{ score: number | null; isActive: boolean }> = ({ score, isActive }) => {
  if (isActive || score === null || score === undefined) {
    return <span className="text-xs text-gray-400">{'\u2014'}</span>;
  }

  const risk = getRiskLevel(score);
  const displayScore = score.toFixed(1);

  return (
    <div className="flex items-center gap-2">
      <div className="flex flex-col items-center">
        <span className={clsx('text-sm font-medium tabular-nums', risk.textColor)}>
          {displayScore}<span className="text-[10px] text-gray-400 dark:text-gray-500 leading-none">/10</span>
        </span>
        
      </div>
    </div>
  );
};

// ─── Findings Cell ──────────────────────────────────────────────────────────

const FindingsCell: React.FC<{
  findings_count: number;
  critical_count: number;
  major_count: number;
  minor_count: number;
  info_count: number;
  isActive: boolean;
}> = ({ findings_count, critical_count, major_count, minor_count, info_count, isActive }) => {
  const { t } = useTranslation();
  if (isActive) {
    return <span className="text-xs text-gray-400">{'\u2014'}</span>;
  }

  if (findings_count === 0) {
    return (
      <div className="flex items-center gap-1.5">
        <PiCheckCircleLight className="w-3.5 h-3.5 text-success-500" />
        <span className="text-xs text-success-600 dark:text-success-400">{t('No issues')}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {critical_count > 0 && (
        <Badge color='red'>
          {critical_count}
        </Badge>
      )}
      {major_count > 0 && (
        <Badge color='orange'>
          {major_count}
        </Badge>
      )}
      {minor_count > 0 && (
        <Badge color='yellow'>
          {minor_count}
        </Badge>
      )}
      {info_count > 0 && (
        <Badge color='blue'>
          {info_count}
        </Badge>
      )}
      <span className="text-[10px] text-gray-400 dark:text-gray-500 ms-0.5 tabular-nums">
        ({findings_count})
      </span>
    </div>
  );
};

// ─── Access / Collaborators Cell ────────────────────────────────────────────

// Rows go to `CollaboratorStack` RAW. This used to read the picture from
// `c.avatar_url` alone, so a review whose collaborators carry it as
// `profile_picture` (which the sign-off and detail payloads do) lost every
// face to a grey silhouette; `accessModel` tries all spellings.
const AccessCell: React.FC<{ collaborators: any[] }> = ({ collaborators }) => {
  if (!collaborators || collaborators.length === 0) {
    return <span className="text-xs text-gray-400">{'\u2014'}</span>;
  }

  return <CollaboratorStack collaborators={collaborators} max={3} />;
};

// ─── Component ──────────────────────────────────────────────────────────────

const ReviewList: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  // Detect ?add=true to show the creation form
  const isAdding = new URLSearchParams(location.search).get('add') === 'true';

  // Current user info for access checks
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const currentUserUuid = userInfo?.UserUID || userInfo?.UserID;

  // Redux state
  const {
    sessionItems,
    sessionTotalItems,
    sessionCurrentPage,
    sessionTotalPages,
    isLoadingSessions,
  } = useSelector((state: RootState) => state.review);

  // Local state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  // The archived view's count rides in the filter bar's own badge (R4-01).
  const [trashCount, setTrashCount] = useState<{ showing: number; total: number }>({ showing: 0, total: 0 });
  const [trashBulkActions, setTrashBulkActions] = useState<{ label: string; onClick: () => void; icon?: React.ReactElement }[]>([]);
  const [trashSelectedCount, setTrashSelectedCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [auditTarget, setAuditTarget] = useState<{ type: string; id: string; name: string } | null>(null);

  // Tour
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'review-list',
    steps: reviewListTourSteps,
    readyToShow: !isLoadingSessions && sessionItems.length > 0,
    autoStartDelay: 600,
  });

  // ─── Live processing status via PubSub ─────────────────────────────────
  const { processingStates, joinProcessingGroups } = useProcessingPubSub({
    entityType: 'review',
    getGroupName: (item: ReviewSessionListItem) => item.uuid,
    getItemUuid: (item: ReviewSessionListItem) => item.uuid,
    isProcessing: (item: ReviewSessionListItem) =>
      item.status === 'processing' || item.status === 'pending',
    onRefetch: () => fetchPage(1),
    enabled: true,
  });

  // ─── Access helpers ─────────────────────────────────────────────────────
  const getUserAccessLevel = useCallback(
    (item: ReviewSessionListItem): string | null => {
      if (!currentUserUuid || !item.has_access) return null;
      const match = item.has_access.find((c) => c.user_id === currentUserUuid);
      return match?.access || null;
    },
    [currentUserUuid],
  );

  const canDeleteItem = useCallback(
    (item: ReviewSessionListItem): boolean => {
      return getUserAccessLevel(item) === 'admin';
    },
    [getUserAccessLevel],
  );

  // ─── Data Fetching ──────────────────────────────────────────────────────

  const fetchPage = useCallback(
    (page: number) => {
      dispatch(
        fetchReviewSessions({
          page,
          page_size: 20,
          status: statusFilter,
          ...(searchTerm ? { search: searchTerm } : {}),
          ...(showDeleted ? { include_deleted: true } : {}),
          sort_by: sortBy,
          sort_order: sortOrder,
        }),
      );
    },
    [dispatch, statusFilter, searchTerm, showDeleted, sortBy, sortOrder],
  );

  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  // Join PubSub groups for processing sessions after fetch
  useEffect(() => {
    if (sessionItems.length > 0) {
      joinProcessingGroups(sessionItems);
    }
  }, [sessionItems, joinProcessingGroups]);

  const hasMore = sessionCurrentPage < sessionTotalPages;

  const loadMore = useCallback(() => {
    if (hasMore && !isLoadingSessions) {
      fetchPage(sessionCurrentPage + 1);
    }
  }, [fetchPage, sessionCurrentPage, hasMore, isLoadingSessions]);

  const handleSearchChange = useCallback(
    debounce((query: string) => {
      setSearchTerm(query);
    }, 300),
    [],
  );

  // ─── Table columns & data ──────────────────────────────────────────────

  /*
    Playbook is its OWN column.

    It used to be the second line of the review's name, stacked with the
    document count - so the one fact that says which RULES a session was run
    against could not be scanned down, could not be compared between rows, and
    shared a line with a number it has nothing to do with. A column can be
    read straight down.

    DOCUMENTS is a column for the same reason, and it was the other half of
    that name stack: "12 docs" sat under the review's title as a subtitle, so
    the one number that says how big a session is could not be compared
    between rows and shared a line with the name it is not part of. It is a
    figure, so it reads as one - beside Findings, which is the other count on
    this row and the one people weigh it against.
  */
  const columns = ['Review', 'Playbook', 'Documents', 'Findings', 'Risk Score', 'Status', 'Access', 'Date', 'Actions'];

  const tableData = useMemo(() => {
    const now = new Date();
    return sessionItems.map((item: ReviewSessionListItem) => {
      const liveState = processingStates[item.uuid];
      const isActive = item.status === 'processing' || item.status === 'pending';
      const isCompleted = item.status === 'completed' || item.status === 'completed_with_errors' || item.status === 'pending_sign_off' || item.status === 'finalized';
      const isFailed = item.status === 'failed';
      const isNew = item.created_at ? (now.getTime() - new Date(item.created_at).getTime()) / 3600000 < 24 : false;
      const isDeleted = !!item.deleted_at;

      return {
        id: item.uuid,
        uuid: item.uuid,
        _raw: item,
        _isDeleted: isDeleted,
        Review: (
          <div className={clsx(isDeleted && 'opacity-50')}>
            <ReviewNameCell
              name={item.name}
              isProcessing={isActive && !isDeleted}
              isFailed={isFailed}
              isCompleted={isCompleted}
              isNew={isNew && isCompleted && !isDeleted}
              liveStep={liveState?.step}
              liveMessage={liveState?.message}
              livePercent={liveState?.percent}
              t={t}
            />
          </div>
        ),
        Playbook: (
          <div className={clsx('min-w-0', isDeleted && 'opacity-50')}>
            {item.playbook_name ? (
              <span
                className="text-sm text-gray-700 dark:text-gray-200 truncate block"
                title={item.playbook_name}
              >
                {item.playbook_name}
              </span>
            ) : (
              /* A session with no playbook is a real state, and a dash says
                 so - an empty cell reads as a rendering fault. */
              <span className="text-sm text-gray-400 dark:text-gray-500">&mdash;</span>
            )}
          </div>
        ),
        Documents: (
          <div className={clsx('text-sm tabular-nums', isDeleted && 'opacity-50')}>
            {item.document_count > 0 ? (
              /* A count in a pill, like every other count in the app - the
                 clause and column counts on the template libraries, the rate
                 history count on the billing matrix. `gray` deliberately: a
                 document count carries no status, so a coloured badge would
                 assert something about the number that is not true. */
              <Badge color="gray">{item.document_count}</Badge>
            ) : (
              /* A session with no documents is a real state; an empty cell
                 reads as a rendering fault. Same dash as Playbook. */
              <span className="text-gray-400 dark:text-gray-500">&mdash;</span>
            )}
          </div>
        ),
        Findings: (
          <div className={clsx(isDeleted && 'opacity-50')}>
            <FindingsCell
              findings_count={item.findings_count}
              critical_count={item.critical_count}
              major_count={item.major_count}
              minor_count={item.minor_count}
              info_count={item.info_count}
              isActive={isActive || isFailed}
            />
          </div>
        ),
        'Risk Score': (
          <div className={clsx(isDeleted && 'opacity-50')}>
            <RiskScoreCell score={item.overall_risk_score} isActive={isActive || isFailed} />
          </div>
        ),
        Status: isDeleted ? (
          <div className="flex items-center gap-1.5">
            <span className="text-danger-400">
              <PiTrashSimpleLight className="w-3.5 h-3.5" />
            </span>
            <Badge color="red">{t('Deleted')}</Badge>
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            {/* <span className={clsx(
              statusColorMap[item.status] === 'blue' && 'text-info-500',
              statusColorMap[item.status] === 'green' && 'text-success-500',
              statusColorMap[item.status] === 'red' && 'text-danger-500',
              statusColorMap[item.status] === 'orange' && 'text-warning-500',
              statusColorMap[item.status] === 'yellow' && 'text-warning-500',
              statusColorMap[item.status] === 'purple' && 'text-purple-500',
              statusColorMap[item.status] === 'indigo' && 'text-indigo-500',
              statusColorMap[item.status] === 'gray' && 'text-gray-400',
            )}>
              {statusIconMap[item.status]}
            </span> */}
            <Badge color={statusColorMap[item.status] || 'gray'}>
              {isActive && liveState?.step
                ? liveState.step.replace(/_/g, ' ')
                : t(statusLabelMap[item.status] || item.status)}
            </Badge>
          </div>
        ),
        Access: (
          <div className={clsx(isDeleted && 'opacity-50')}>
            <AccessCell collaborators={item.has_access} />
          </div>
        ),
        Date: isDeleted ? (
          <div className="flex flex-col">
            <span className="text-xs text-danger-400 dark:text-danger-500 tabular-nums">
              Deleted {formatRelativeTime(item.deleted_at!)}
            </span>
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              Created {formatDateTime(item.created_at as string)}
            </span>
          </div>
        ) : (
          <div className="flex flex-col">
            <span className="text-xs text-gray-700 dark:text-gray-300 tabular-nums">
              {formatDateTime(item.created_at as string)}
            </span>
            {/* {item.completed_at && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                Completed {formatRelativeTime(item.completed_at)}
              </span>
            )} */}
          </div>
        ),
      };
    });
  }, [sessionItems, processingStates, t]);

  /*
    Playbook is deliberately NOT here.

    Sorting is server-side (`sort_by` on the sessions endpoint), and the API
    has no `sort_by=playbook_name`. A header that sorted only the page in hand
    would reorder twenty rows out of two hundred and call it sorted, which is
    worse than not offering it. Backend follow-up, then this list gains one
    entry and nothing else changes.
  */
  const sortableColumns = ['Review', 'Status', 'Risk Score', 'Date'];

  // ─── Actions ────────────────────────────────────────────────────────────

  const handleRowClick = useCallback(
    (row: any) => {
      if (row?._isDeleted) return; // Don't navigate to deleted items
      if (row?.uuid) {
        history.push(`/reviews/${row.uuid}`);
      }
    },
    [history],
  );

  const handleDeleteReviews = useCallback(
    (reviewIds: string[]) => {
      if (reviewIds.length === 0) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Please select at least one review session.'),
            modalType: 'warning',
            duration: 3000,
          }),
        );
        return;
      }

      // Check if the user has admin access to all selected reviews
      const nonAdminIds = reviewIds.filter((id) => {
        const item = sessionItems.find((s) => s.uuid === id);
        return item ? !canDeleteItem(item) : true;
      });

      if (nonAdminIds.length > 0) {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('You do not have admin access to delete some of the selected reviews.'),
            modalType: 'error',
            duration: 5000,
          }),
        );
        return;
      }

      // Gather names for display
      const reviewNames = reviewIds.map((id) => {
        const item = sessionItems.find((s) => s.uuid === id);
        return item?.name || null;
      }).filter(Boolean) as string[];

      const isPlural = reviewIds.length > 1;

      dispatch(
        showModal({
          type: 'custom',
          message: isPlural
            ? t(`Delete ${reviewIds.length} review sessions?`)
            : t('Delete this review session?'),
          subMsg: t('This action cannot be undone.'),
          icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
          content: (
            <DeleteReviewConfirmation
              reviewCount={reviewIds.length}
              reviewNames={reviewNames}
              onConfirm={async () => {
                let result: any;
                if (reviewIds.length > 1) {
                  result = await dispatch(bulkDeleteReviewSessions(reviewIds) as any);
                } else {
                  result = await dispatch(deleteReviewSession(reviewIds[0]) as any);
                }
                if (result?.success) {
                  setSelectedIds([]);
                  fetchPage(1);
                }
                dispatch(hideModal());
              }}
              onCancel={() => dispatch(hideModal())}
            />
          ),
          onCancel: () => {},
          showModal: true,
        }),
      );
    },
    [dispatch, t, sessionItems, canDeleteItem, fetchPage],
  );

  const handleRename = useCallback(
    async (row: any) => {
      if (!row?.uuid) return;
      const rawItem: ReviewSessionListItem | undefined = row._raw;
      const accessLevel = rawItem ? getUserAccessLevel(rawItem) : null;

      if (accessLevel !== 'admin' && accessLevel !== 'edit') {
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('You do not have permission to rename this review.'),
            modalType: 'error',
            duration: 4000,
          }),
        );
        return;
      }

      const currentName = rawItem?.name || '';
      const newName = window.prompt(
        'Enter a new name for this review session:',
        currentName,
      );
      if (newName !== null && newName.trim()) {
        await dispatch(renameReviewSession(row.uuid, newName.trim()) as any);
      }
    },
    [dispatch, t, getUserAccessLevel],
  );

  const handleSelect = useCallback(
    (id: string) => {
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
      );
    },
    [],
  );

  const handleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.length === sessionItems.length ? [] : sessionItems.map((i) => i.uuid),
    );
  }, [sessionItems]);

  const handleRestoreReview = useCallback(
    async (row: any) => {
      if (!row?.uuid) return;
      const result = await dispatch(restoreReviewSession(row.uuid) as any);
      if (result?.success) {
        fetchPage(1);
      }
    },
    [dispatch, fetchPage],
  );

  const tableActions = useMemo(
    () => [
      {
        label: 'Activity',
        onClick: (row: any) => setAuditTarget({ type: 'review', id: row.uuid || row.id, name: row._raw?.name || '' }),
        icon: <PiClockCounterClockwiseLight className="w-4 h-4" />,
        hidden: (row: any) => !!row?._isDeleted,
      },
      {
        label: 'Restore',
        onClick: handleRestoreReview,
        icon: <PiArrowCounterClockwiseLight className="w-4 h-4" />,
        hidden: (row: any) => !row?._isDeleted,
      },
      {
        label: 'View',
        onClick: (row: any) => row?.uuid && history.push(`/reviews/${row.uuid}`),
        icon: <PiEyeLight className="w-4 h-4" />,
        hidden: (row: any) => !row?._isDeleted,
      },
      {
        label: 'Rename',
        onClick: handleRename,
        icon: <PiPencilSimpleLight className="w-4 h-4" />,
        hidden: (row: any) => !!row?._isDeleted,
      },
      {
        label: 'Delete',
        onClick: (row: any) => handleDeleteReviews([row.uuid]),
        icon: <PiTrashLight className="w-4 h-4" />,
        isDestructive: true,
        hidden: (row: any) => !!row?._isDeleted,
      },
    ],
    [handleRename, handleDeleteReviews, handleRestoreReview, history],
  );

  const filterBarActions = useMemo(() => {
    const actions = [];
    if (selectedIds.length > 0) {
      actions.push({
        label: `Delete (${selectedIds.length})`,
        onClick: () => handleDeleteReviews(selectedIds),
        icon: <PiTrashLight className="w-4 h-4" />,
      });
    }
    return actions;
  }, [selectedIds, handleDeleteReviews]);

  // ─── Filter sections for FilterBarWithTabs ──────────────────────────────

  const filterSections = useMemo(
    () => [
      {
        id: 'status',
        name: t('Status'),
        options: [
          { label: t('In review'), value: 'completed' },
          { label: t('Awaiting sign-off'), value: 'pending_sign_off' },
          { label: t('Finalized'), value: 'finalized' },
          { label: t('Processing'), value: 'processing' },
          { label: t('Queued'), value: 'pending' },
          { label: t('Failed'), value: 'failed' },
        ],
      },
      {
        id: 'visibility',
        name: t('Visibility'),
        options: [
          { label: t('Include deleted'), value: 'include_deleted' },
        ],
      },
    ],
    [t],
  );

  const selectedFilters = useMemo(
    (): { [key: string]: string[] } => ({
      ...(statusFilter ? { status: [statusFilter] } : {}),
      ...(showDeleted ? { visibility: ['include_deleted'] } : {}),
    }),
    [statusFilter, showDeleted],
  );

  const handleFilterChange = useCallback(
    (filterName: string, selectedValues: string[]) => {
      if (filterName === 'status') {
        setStatusFilter(selectedValues.length > 0 ? selectedValues[0] : undefined);
      }
      if (filterName === 'visibility') {
        setShowDeleted(selectedValues.includes('include_deleted'));
      }
    },
    [],
  );

  const handleClearAllFilters = useCallback(() => {
    setStatusFilter(undefined);
    setShowDeleted(false);
  }, []);

  const handleSortChange = useCallback(
    (newSortBy: string, newSortOrder?: 'asc' | 'desc') => {
      if (newSortOrder) {
        setSortBy(newSortBy);
        setSortOrder(newSortOrder);
      } else {
        const parts = newSortBy.split('_');
        const order = parts.pop() as 'asc' | 'desc';
        const field = parts.join('_');
        setSortBy(field);
        setSortOrder(order);
      }
    },
    [],
  );

  // ─── Render ─────────────────────────────────────────────────────────────

  if (isAdding) {
    return (
      // Same bounded-height reasoning as the tables create branch: <main> has no
      // definite height on non-scrollable routes, so `h-full` here resolved to
      // auto and the form's footer could be pushed past the viewport.
      <div className="flex flex-col overflow-hidden relative h-screen min-h-0">
        <div className="flex-1 min-h-0">
          <StartReviewForm />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-hidden relative h-full">
      <div data-tour-id="review-list-new-button">
        <Header
          actions={[
            {
              label: t('Add review'),
              icon: <PiPlusLight className="w-4 h-4" />,
              onClick: () => history.push('/reviews?add=true'),
            },
          ]}
        />
      </div>

      <div data-tour-id="review-list-filter-bar">
      <FilterBarWithTabs
        itemCount={showTrash ? trashCount.showing : sessionItems.length}
        totalItemCount={showTrash ? trashCount.total : sessionTotalItems}
        itemCountLabel={showTrash ? 'items' : 'reviews'}
        listLoading={isLoadingSessions && sessionItems.length === 0}
        filters={filterSections}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        onClearAllFilters={handleClearAllFilters}
        onSearchChange={handleSearchChange}
        // onRefresh={() => fetchPage(1)}
        // itemCount={sessionItems.length}
        // totalItemCount={sessionTotalItems}
        // itemCountLabel="sessions"
        actions={[
          ...filterBarActions,
          ...(showTrash ? trashBulkActions : []),
        ]}
        selectionCount={showTrash ? trashSelectedCount : (selectedIds.length > 0 ? selectedIds.length : undefined)}
        recycleBinEnabled
        recycleBinActive={showTrash}
        recycleBinLabel={t('Reviews')}
        onRecycleBinToggle={(active) => { if (!active) fetchPage(1); setShowTrash(active); }}
      />
      </div>

      {showTrash ? (
        <ResourceTrash
          onCountChange={(showing, total) => setTrashCount({ showing, total })}
          entityType="review_session"
          entityLabel="reviews"
          emptyIcon={<PiFileMagnifyingGlassLight />}
          onItemRestored={() => fetchPage(1)}
          onBulkActionsChange={setTrashBulkActions}
          onSelectedCountChange={setTrashSelectedCount}
        />
      ) : (
      /*
        ONE SCROLLER, and it is the table's. Same fix as the tables list.

        This box was `flex-grow overflow-y-auto` - a second scroll region
        wrapped around the one the table already has - so the page and the
        table disagreed about how tall the list was: two scrollbars, the
        column header scrolling off the top instead of sticking, and dead
        space under the rows.

        A plain bounded flex column: `flex-1 min-h-0` from here down through
        the fade wrapper to the table, so the table fills exactly what is left
        and does its own scrolling with its header stuck to the top of a
        region a reader can actually see.
      */
      <div className="flex-1 min-h-0 relative flex flex-col">
        {/* Tour anchor: small overlay covering table header + first row area */}
        <div
          data-tour-id="review-list-table"
          className="absolute inset-x-0 top-0 pointer-events-none"
          style={{ height: 120 }}
        />
        <div className="animate-fade-in flex-1 min-h-0 flex flex-col">
          <Table
            columns={columns}
            data={tableData}
            onRowClick={handleRowClick}
            enableSelection
            selectedIds={selectedIds}
            onSelect={handleSelect}
            onSelectAll={handleSelectAll}
            actions={tableActions}
            hasMore={hasMore}
            isLoadingMore={isLoadingSessions}
            isLoading={isLoadingSessions}
            onLoadMore={loadMore}
            sortableColumns={sortableColumns}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSortChange}
            total_items={sessionTotalItems}
            total_showing_items={sessionItems.length}
            setIsShowing={true}
            showingLabel={t('reviews')}
            selectedCount={selectedIds.length}
            resizableColumns
            storageKeyPrefix="review-sessions-v2"
            /*
              No viewport-relative cap. The default `100vh - 150px` is a GUESS
              at the chrome above this list; now that the chain above is
              properly bounded, `flex-1 min-h-0` sizes the region to exactly
              what is left, which is the measurement rather than an estimate.
            */
            max="max-h-none"
            defaultColumnWidths={{
              Review: 320,
              Playbook: 180,
              Documents: 110,
              Findings: 200,
              'Risk Score': 150,
              Status: 160,
              Access: 100,
              Date: 150,
            }}
            emptyState={{
              icon: <PiFileMagnifyingGlassLight className="w-12 h-12" />,
              headline: t('No review sessions yet'),
              subtext: t(
                'Start a new review to analyze contracts against your playbook standards.',
              ),
              button: {
                label: t('Start first review'),
                onClick: () => history.push('/reviews?add=true'),
              },
            }}
          />
        </div>
      </div>
      )}
      {/* Audit Log Side Panel */}
      {auditTarget && (
        <ActivityDrawer onClose={() => setAuditTarget(null)}>
          <AuditLogPanel
            entityType="review"
            entityId={auditTarget.id}
            entityName={auditTarget.name}
            onClose={() => setAuditTarget(null)}
          />
        </ActivityDrawer>
      )}

      <FeatureTour
        steps={reviewListTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
};

export default ReviewList;
